<template>
  <div v-if="products.length > 0" class="complete-the-look">
    <h2 class="heading">Complete The Set</h2>

    <div class="set container container--narrow">
      <form class="form" @submit.prevent="handleFormSubmit">
        <SetProduct
          v-for="(product, index) in orderedProducts"
          :key="product.id"
          :product="product"
          :class="`product-${index}`"
          @variantSelected="
            (variant) => handleVariantSelected(variant, product.id)
          "
        />
        <div class="actions">
          <p class="total">Total: {{ formattedTotalPrice }}</p>

          <Button
            :loading="loading"
            type="submit"
            full-width
            class="add-set-to-bag-btn"
          >
            Add To Bag
          </Button>

          <BNPLInformation :price="totalPrice" />
        </div>
      </form>
    </div>
  </div>

  <AddedSetToBag
    v-model:open="isAddedSetToBagOpen"
    :variants="selectedVariants"
  />
</template>

<script>
import productIdQuery from "@/graphql/queries/ProductId.gql";
import Button from "@/components/Button.vue";
import SetProduct from "@/components/product-display/complete-the-set/SetProduct.vue";
import useVuelidate from "@vuelidate/core";
import { mapActions } from "vuex";
import cloneDeep from "lodash-es/cloneDeep";
import BNPLInformation from "@/components/product-display/BNPLInformation.vue";
import AddedSetToBag from "@/components/product-display/AddedSetToBag.vue";

export default {
  name: "CompleteTheSet",
  components: {
    Button,
    SetProduct,
    BNPLInformation,
    AddedSetToBag,
  },
  props: {
    productId: {
      type: String,
      required: true,
    },
  },
  setup() {
    const v = useVuelidate();

    return { v };
  },
  data() {
    return {
      products: [],
      selectedVariants: {},
      loading: false,
      isAddedSetToBagOpen: false,
    };
  },
  computed: {
    formattedTotalPrice() {
      return this.$filters.toCurrency(
        this.totalPrice.amount,
        this.totalPrice.currencyCode,
        0
      );
    },
    totalPrice() {
      const total = this.products.reduce((acc, product) => {
        return acc + parseFloat(product.priceRange.minVariantPrice.amount);
      }, 0);

      return {
        amount: total,
        currencyCode: this.products[0].priceRange.minVariantPrice.currencyCode,
      };
    },
    orderedProducts() {
      const products = cloneDeep(this.products);
      return products.sort((a, b) => a.order - b.order);
    },
  },
  async created() {
    const groupedProductIds = await this.fetchGroupedProductIds();
    await this.getProducts(groupedProductIds);
  },
  methods: {
    ...mapActions("cart", ["addCartItem", "checkCartRules"]),
    async fetchGroupedProductIds() {
      const url = process.env.PRODUCT_GROUPING_GADGET_URL + this.productId;

      const resp = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) {
        throw new Error("Error fetching grouped products");
      }

      const data = await resp.json();

      return data?.groupedProducts || [];
    },
    async getProducts(products) {
      const promises = products.map(({ id }) => {
        return this.$apollo.query({
          query: productIdQuery,
          variables: {
            productId: "gid://shopify/Product/" + id,
          },
        });
      });

      const values = await Promise.all(promises);
      values.forEach((value) => {
        const product = value.data.product;

        const order = products.find(
          (p) => "gid://shopify/Product/" + p.id === product.id
        ).order;

        this.products.push({ ...product, order });
      });
    },
    async handleFormSubmit() {
      this.v.$touch();

      if (this.v.$invalid) {
        return;
      }

      const variantIds = Object.values(this.selectedVariants);

      this.loading = true;

      for (const variantId of variantIds) {
        await this.addCartItem({ id: variantId });
      }

      this.isAddedSetToBagOpen = true;
      this.loading = false;
    },
    handleVariantSelected(variantId, productId) {
      this.selectedVariants[productId] = variantId;
    },
  },
};
</script>

<style lang="scss" scoped>
.complete-the-look {
  padding: 30px 15px;
  margin-bottom: 15px;
  margin-top: 15px;

  @include respond-above(md) {
    padding: 60px 15px;
  }
}

.set {
  padding: 0;
}

.heading {
  @include t__editorial-headline-20;
  text-align: center;
  margin-bottom: 30px;
}

.form {
  @include respond-above(md) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 90px;

    .product {
      grid-column: 1 / 3;
    }

    .actions {
      grid-column: 1/3;
    }
  }
}

.actions {
  text-align: center;
  max-width: 380px;
  width: 100%;
  margin: 0 auto;

  @include respond-above(md) {
    margin-top: 30px;
  }
}

.total {
  @include t__utility-label-30;
  margin-bottom: 15px;
}

.add-set-to-bag-btn {
  margin-bottom: 16px;
}
</style>
